import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { Button, Select, Option } from 'src/components/PulseUI';

interface TableFooterProps {
  page: number;
  pageSize: number;
  rowCount: number;
  rowsPerPageOptions?: number[];
  onPageChange: (e: any, newPage: number) => void;
  onPageSizeChange: (e: any) => void;
}

const TableFooter = ({
  page,
  pageSize,
  rowCount,
  rowsPerPageOptions = [50, 75, 100],
  onPageChange,
  onPageSizeChange,
}: TableFooterProps) => {
  const totalPages = Math.ceil(rowCount / pageSize);
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, rowCount);

  const handlePreviousPage = () => {
    if (page > 0) {
      onPageChange(null, page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      onPageChange(null, page + 1);
    }
  };

  const handlePageSizeChange = (e: React.MouseEvent, value: string | number) => {
    if (value) {
      // Create a synthetic event with the new value
      const syntheticEvent = {
        target: { value: Number(value) },
      };
      onPageSizeChange(syntheticEvent);
    }
  };

  return (
    <div className="sticky bottom-0 bg-white z-[1]" data-testid="table-footer">
      <nav
        className="flex justify-end items-center gap-3 p-1 px-2 border-t border-neutral-300 w-full min-h-10 bg-white"
        role="navigation"
        aria-label="Table pagination"
      >
        <div className="flex items-center gap-2">
          <label
            htmlFor="rows-per-page-select"
            className="text-xs text-neutral-600"
          >
            Rows per page:
          </label>
          <Select
            id="rows-per-page-select"
            size="sm"
            variant="outlined"
            className="min-w-[70px]"
            value={pageSize}
            onChange={handlePageSizeChange}
            aria-label="Select rows per page"
          >
            {rowsPerPageOptions.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        </div>

        <div className="text-xs text-neutral-600" aria-live="polite">
          {rowCount > 0 ? (
            <>
              {startRow}-{endRow} of {rowCount}
            </>
          ) : (
            '0 of 0'
          )}
        </div>

        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Page navigation"
        >
          <Button
            variant="plain"
            color="neutral"
            className="p-1"
            onClick={handlePreviousPage}
            disabled={page === 0}
            aria-label={`Go to previous page${page > 0 ? `, page ${page}` : ''}`}
            data-testid="pagination-prev"
          >
            <Icon icon={faChevronLeft} className="w-3 h-3" aria-hidden="true" />
          </Button>

          <Button
            variant="plain"
            color="neutral"
            className="p-1"
            onClick={handleNextPage}
            disabled={page >= totalPages - 1}
            aria-label={`Go to next page${page < totalPages - 1 ? `, page ${page + 2}` : ''}`}
            data-testid="pagination-next"
          >
            <Icon icon={faChevronRight} className="w-3 h-3" aria-hidden="true" />
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default TableFooter;
